import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

class DayNight {
	constructor (container) {
		// All dom elements
		this.el = {
			container: container,
			parent: document.querySelector('.day-night-animation'),
			wrapper: document.querySelector('.day-night-wrapper'),
			nav: document.querySelector('#navbar_wrapper'),
			sun: container.querySelector('.sun'),
			moon: container.querySelector('.moon'),
			sky: container.querySelector('.sky'),
			backBuilding: container.querySelector('.back_building'),
			midBuilding: container.querySelector('.mid_building'),
			dayText: document.querySelector('.day-night-text-day'),
			nightText: document.querySelector('.day-night-text-night'),
			toggleDayTitle: document.querySelector('.toggle-day-night-day'),
			toggleNightTitle: document.querySelector('.toggle-day-night-night'),
			copy: document.querySelector('.day-night-copy'),
			toggle: document.querySelector('.day-night-toggle'),
			textToggle: document.querySelector('.day-night-text-toggle')
		};
		this.tl = null;
		this.sunTween = null;
		this.isCopyVisible = null;
		this.containerHeight = false;

		let prevHeight = window.innerHeight;
		let prevWidth = window.innerWidth;
		window.addEventListener('resize', () => {
			// This is to avoid jumps when scrolling on iOs and making the UI slide down triggering a resize event
			if (
				(prevWidth !== window.innerWidth) || // If the width changed -> Update
				(prevHeight < window.innerHeight) // If the height of the window is bigger -> Update
				) {
				this.setContainerHeight();
				this.toggleContentVisibility();
				this.tl.scrollTrigger.refresh();
				this.sunTween.scrollTrigger.refresh();
			}
			this.setPopupOffset();
			// Update the previous size dimensions
			prevHeight = window.innerHeight;
			prevWidth = window.innerWidth;
		});

		// Hide the night text and toggle by default
		this.el.nightText.style.display = 'none';
		this.el.toggleNightTitle.style.display = 'none';

		this.setContainerHeight();
		this.setPopupOffset();
		this.toggleContentVisibility();
		this.setupMobilePanel();
		this.setupTimeline();
	}

	setupTimeline () {
		/* Make the sun appear when in viewport */
		this.sunTween = gsap.fromTo(this.el.sun, {
			rotation: -10,
			svgOrigin: '100 1800'
		}, {
			rotation: 90,
			ease: 'power1.inOut',
			scrollTrigger: {
				trigger: this.el.container.parentElement,
				scrub: 0.2,
				start: `top bottom`,
				end: () => {
					let totalHeight = 0;
					if (this.isMobile()) {
						// On mobile, 2.5 * (max height available);
						totalHeight = this.containerHeight * 2.5;
					} else {
						// On desktop, always 2.5 * window height
						totalHeight = window.innerHeight * 2.5;
					}
					return `+=${totalHeight}`;
				}
			}
		});

		let titleTextWidth = this.el.toggleDayTitle.offsetWidth;
		/* Animation of text from day to night */
		const switchToNightTl = gsap.timeline({paused: true})
			.fromTo([this.el.dayText, this.el.toggleDayTitle], {
				opacity: 1
			}, {
				opacity: 0,
				duration: 0.5,
				ease: 'power1.in'
			})
			.call(() => {
				titleTextWidth = this.el.toggleDayTitle.offsetWidth;
			})
			.set([this.el.dayText, this.el.toggleDayTitle], {
				display: 'none'
			})
			.set([this.el.nightText, this.el.toggleNightTitle], {
				display: 'inline-block'
			})
			.call(() => {
				// Make the word 'nuits' larger so it doesn't wrap on previous line
				if (titleTextWidth > this.el.toggleNightTitle.offsetWidth) {
					this.el.toggleNightTitle.style.marginRight = titleTextWidth - this.el.toggleNightTitle.offsetWidth + 'px';
				}
			})
			.fromTo([this.el.nightText, this.el.toggleNightTitle], {
				opacity: 0
			}, {
				opacity: 1,
				duration: 1,
				ease: 'power2.inOut'
			});

		/* Animation of text from night to day */
		const switchToDayTl = gsap.timeline({paused: true})
			.fromTo([this.el.nightText, this.el.toggleNightTitle], {
				opacity :1
			}, {
				opacity: 0,
				duration: 0.5,
				ease: 'power1.in'
			})
			.set([this.el.nightText, this.el.toggleNightTitle], {
				display: 'none'
			})
			.set([this.el.dayText, this.el.toggleDayTitle], {
				display: 'inline-block'
			})
			.fromTo([this.el.dayText, this.el.toggleDayTitle], {
				opacity: 0
			}, {
				opacity: 1,
				duration: 1,
				ease: 'power2.inOut'
			});

		// Quick hack because GSAP is applying the 'from' values on the elements with paused timelines
		gsap.set([this.el.dayText, this.el.toggleDayTitle], {
			opacity: 1
		});

		/* Main timeline */
		this.tl = gsap.timeline({
			scrollTrigger: {
				trigger: this.el.container.parentElement,
				pin: true,
				scrub: 0.2,
				start: () => {
					let topOffset = this.el.nav.offsetHeight;
					if (!this.isMobile()) {
						topOffset += parseFloat(window.getComputedStyle(this.el.parent).marginTop);
					}
					return `top ${topOffset}px`;
				},
				end: () => {
					return `+=${window.innerHeight * 3}`
				}
			}
		});

		/* Make the moon visible */
		this.tl.fromTo(this.el.moon, {
			rotation: -25,
			svgOrigin: '100 1800'
		}, {
			rotation: () => {
				return this.isMobile() ? 10 : 0
			},
			duration: 0.15,
			ease: 'linear'
		}, 0.06);

		/* SKY SWITCH */
		this.tl.fromTo(this.el.sky, {
			fill: '#82d7ff'
		}, {
			fill: '#00053C',
			duration: 0.1
		}, 0.04);

		/* BACK BUILDING SWITCH */
		this.tl.fromTo(this.el.backBuilding, {
			fill: '#ffb4d2'
		}, {
			fill: '#82d7ff',
			duration: 0.3
		}, 0.04);

		/* MIDDLE BUILDING SWITCH */
		this.tl.fromTo(this.el.midBuilding, {
			fill: 'rgb(50,50,150)'
		}, {
			fill: '#00a05a',
			duration: 0.3
		}, 0.04);

		this.tl.call(() => {
			/* Switch text to night when scrolling down */
			if (this.tl.scrollTrigger.direction === 1) {
				switchToNightTl.play(0);
			}
			/* Switch back to day when scrolling up */
			if (this.tl.scrollTrigger.direction === -1) {
				switchToDayTl.play(0);
			}
		}, null, 0.1);
	}

	/* Mobile interaction with the box */
	setupMobilePanel () {
		/* When clicking on the arrow or the 'Read more' buttons */
		this.el.textToggle.addEventListener('click', () => this.onToggleClick());
		if (this.el.toggle) {
			this.el.toggle.addEventListener('click', () => this.onToggleClick());
		}
	}

	toggleContentVisibility () {
		if (this.isMobile() && this.isCopyVisible === null) {
			// On page load, hide the copy
			gsap.set(this.el.copy, {
				height: 0,
				overflow: 'hidden'
			});
		} else if (!this.isMobile()) {
			// On desktop, always show the copy
			gsap.set(this.el.copy, {
				height: 'auto',
				overflow: 'hidden'
			});
		}
	}

	onToggleClick () {
		/* If copy was visible, close it */
		if (this.isCopyVisible) {
			gsap.to(this.el.copy, {
				height: 0,
				duration: 0.5,
				ease: 'power1.out'
			});
			this.isCopyVisible = false;
			// Update the copy of the button
			this.el.textToggle.innerHTML = this.el.textToggle.dataset.more;
		} else {
			/* Expand the copy */
			gsap.to(this.el.copy, {
				height: 'auto',
				duration: 0.5,
				ease: 'power1.out'
			});
			this.isCopyVisible = true;
			// Update the copy of the button
			this.el.textToggle.innerHTML = this.el.textToggle.dataset.less;
		}

		// Add a class on the parent to make the buttons arrows rotate
		this.el.parent.classList.toggle('is-copy-expanded', this.isCopyVisible);
	}

	/* Detect if the current screen matches the mobile media query */
	isMobile () {
		return window.matchMedia('(min-width: 0em) and (max-width: 57.5525em)').matches;
	}

	/* Define the height of the container */
	setContainerHeight () {
		if (this.isMobile()) {
			this.el.container.style.height = (window.innerHeight - this.el.nav.offsetHeight) + 'px';
			this.containerHeight = window.innerHeight;
		} else {
			// On desktop the height is automatic
			this.el.container.style.height = '';
			this.containerHeight = false;
		}
	}

	/* Define the bottom position of tooltips */
	setPopupOffset () {
		// This offset is needed when the animation height is bigger than the window height on mobile
		if (this.containerHeight) {
			this.el.parent.style.setProperty('--bottom-offset', (this.containerHeight - window.innerHeight) + 'px');
		}
	}
}

export default DayNight;
